import React, {
	useCallback,
	useContext,
	useState,
	useRef,
	useEffect
} from "react";
import { Link } from "react-router-dom";

import { AlertContext } from "components/AlertProvider/AlertProvider";
import { ApiRequest } from "api/makeApiRequest/ApiRequest";
import { Auth } from "api/makeApiRequest";
import { useRemoteResource } from "hooks/useRemoteResource";
import { useDefaultTimezone } from "hooks/useDefaultTimezone";
import { useSendData, Method } from "hooks/useSendData";
import { useHandleItemFromURL } from "hooks/useHandleItemFromURL";
import { useStripQueryStringFromAddressBar } from "hooks/useStripQueryStringFromAddressBar";
import { useMenuButtonOpenChat } from "hooks/useMenuButtonOpenChat";
import { useMenuButtonNavigateToProfile } from "hooks/useMenuButtonNavigateToProfile";
import ErrorBanner from "components/ErrorBanner";
import OpenChatAtMessageButton from "components/OpenChatAtMessageButton";
import Table from "components/Table";
import { captureException } from "services/captureException";
import { AuthContext } from "components/AuthProvider/AuthProvider";
import { DashboardContext } from "components/PageDashboard/PageDashboard";
import { SearchResults } from "types-and-validators/SearchResults";
import { TableContainer } from "./RequestsSection.styles";

type Item = {
	id: string;
	hostUid: string;
	guestUid?: string;
	cacheInMemory?: boolean;
	isAccepted: boolean;
	isRejected: boolean;
	privateMessageId?: string;
	privateMessageContents?: string;
	privateMessageDatesentUTC?: Date;
	displayNameGuest: string;
	displayNameHost: string;
};

type ResponseModalParams<ItemData> = {
	setCurrentResponse: React.Dispatch<React.SetStateAction<boolean | undefined>>;
	setCurrentResponseNote: React.Dispatch<
		React.SetStateAction<string | undefined>
	>;
	setRespondingTo: React.Dispatch<React.SetStateAction<ItemData | undefined>>;
	respondingTo: ItemData | undefined;
	currentResponse: boolean | undefined;
	sendResponse: Method<
		{
			id: string;
			response: boolean;
			message?: string | undefined;
		},
		{}
	>;
	currentResponseApiRequest: React.MutableRefObject<
		ApiRequest<unknown> | undefined
	>;
	currentResponseNote: string | undefined;
	setCurrentResponseApiRequestDispatchTime: React.Dispatch<
		React.SetStateAction<number | undefined>
	>;
};

type Props<ItemData extends Item> = {
	fetchRequests: (
		{
			hostUid,
			guestUid,
			cacheInMemory,
			isAccepted,
			isRejected
		}: Pick<
			ItemData,
			"hostUid" | "guestUid" | "cacheInMemory" | "isAccepted" | "isRejected"
		> & { cacheInMemory?: boolean },
		auth: Auth
	) => ApiRequest<SearchResults<ItemData>>;
	queryStringKey: string;
	dashboardContextKey: string;
	respondToItem: (
		data: { id: string; response: boolean; message?: string },
		auth: Auth
	) => ApiRequest<{}>;
	cols: { key: string; heading: string }[];
	formatItemDetails: (
		arg: ItemData,
		uid: string,
		useShortFormat: boolean,
		timezone: string
	) => { hostUid: string; partnersUid: string; partnersName: string };
	responseModal: (arg: ResponseModalParams<ItemData>) => React.ReactNode;
	itemsDescription: string;
};

function RequestsSection<ItemData extends Item>({
	fetchRequests,
	queryStringKey,
	dashboardContextKey,
	respondToItem,
	cols,
	formatItemDetails,
	responseModal,
	itemsDescription
}: Props<ItemData>) {
	const [{ isSignedIn, uid }] = useContext(AuthContext);
	const [, setDashboardContextState] = useContext(DashboardContext);

	const [addAlert] = useContext(AlertContext);

	const [repeatNum, setRepeatNum] = useState(1);
	const { isWaiting, isLoading, isError, output: items } = useRemoteResource<
		ItemData[]
	>(
		useCallback(() => {
			if (!uid) {
				throw new Error("No UID");
			}

			const req = fetchRequests(
				{
					hostUid: uid,
					isAccepted: false,
					isRejected: false
				},
				{ uid }
			);

			return {
				...req,
				ready: req.ready.then(result => {
					if (!result) {
						return [];
					}
					return result.page;
				})
			};
		}, [uid, fetchRequests]),
		isSignedIn === undefined,
		repeatNum,
		true
	);

	const [respondingTo, setRespondingTo] = useState<ItemData | undefined>(
		undefined
	);
	const [currentResponse, setCurrentResponse] = useState<boolean | undefined>(
		undefined
	);
	const [currentResponseNote, setCurrentResponseNote] = useState<
		string | undefined
	>(undefined);

	const currentResponseApiRequest = useRef<ApiRequest<unknown> | undefined>(
		undefined
	);
	const [
		currentResponseApiRequestDispatchTime,
		setCurrentResponseApiRequestDispatchTime
	] = useState<number | undefined>(undefined);
	useEffect(() => {
		const { current: req } = currentResponseApiRequest;
		if (currentResponseApiRequestDispatchTime && req) {
			req.ready.then(response => {
				addAlert({ contents: "Response received!  Thank you" });

				setCurrentResponse(undefined);
				setCurrentResponseNote(undefined);
				setRespondingTo(undefined);
				setRepeatNum(new Date().getTime());
				setDashboardContextState(oldState => ({
					...oldState,
					dashboardContextKey: new Date().getTime()
				}));
			});

			return () => {
				req.abort();
			};
		}
	}, [
		addAlert,
		currentResponseApiRequest,
		currentResponseApiRequestDispatchTime,
		setCurrentResponse,
		setCurrentResponseNote,
		setRespondingTo,
		setRepeatNum,
		setDashboardContextState
	]);

	const stripQueryStringFromAddressBar = useStripQueryStringFromAddressBar();
	useHandleItemFromURL<ItemData>({
		key: queryStringKey,
		itemsList: {
			isWaiting,
			isLoading,
			isError,
			items
		},
		handleFoundItem: useCallback(
			item => {
				if (!("hostUid" in item)) {
					throw captureException(new Error("Item in URL lacks hostUid"), {
						evtType: "itemInURLNoHostUid",
						extra: { item }
					});
				} else {
					setRespondingTo(item);
				}
				stripQueryStringFromAddressBar();
			},
			[setRespondingTo, stripQueryStringFromAddressBar]
		)
	});

	const sendResponse = useSendData({ method: respondToItem });

	const menuButtonOpenChat = useMenuButtonOpenChat();
	const menuButtonNavigateToProfile = useMenuButtonNavigateToProfile();

	const defaultTimezone = useDefaultTimezone();

	const rows =
		items && items.length && uid
			? items.map(item => {
					const {
						hostUid,
						partnersUid,
						partnersName,
						...rest
					} = formatItemDetails(item, uid, true, defaultTimezone);

					return {
						data: {
							...rest,
							partner: (
								<Link to={`/user-id/${partnersUid}`}>{partnersName}</Link>
							),
							message: item.privateMessageContents ? (
								<>
									{" "}
									<OpenChatAtMessageButton
										messageId={item.privateMessageId}
										text={item.privateMessageContents}
										partnersUid={partnersUid}
										patnersDisplayName={partnersName}
										dateSent={item.privateMessageDatesentUTC}
									/>{" "}
								</>
							) : null
						},
						action: {
							onClick: () => setRespondingTo(item),
							label: "Respond"
						},
						menu: [
							menuButtonOpenChat({
								uid: partnersUid,
								displayName: partnersName
							}),
							...(hostUid === uid
								? []
								: [menuButtonNavigateToProfile({ uid: partnersUid })])
						]
					};
			  })
			: [];

	return (
		<>
			{responseModal({
				currentResponseNote,
				setCurrentResponseNote,
				currentResponseApiRequest,
				setCurrentResponse,
				setCurrentResponseApiRequestDispatchTime,
				setRespondingTo,
				respondingTo,
				currentResponse,
				sendResponse
			})}
			{isError ? <ErrorBanner /> : null}
			{!isError ? (
				<TableContainer isLoading={isLoading || isWaiting}>
					<Table
						cols={cols}
						rows={rows}
						emptyText={`There are no ${itemsDescription} that need your attention`}
						isLoading={isLoading || isWaiting}
					/>
				</TableContainer>
			) : null}
		</>
	);
}

export default RequestsSection;
